import React, { useState } from 'react';
import styled from 'styled-components';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiCheck, FiX, FiAlertCircle, FiKey } from 'react-icons/fi';
import Header from '../components/Header';
import authService from '../services/authService';
console.log('🔥🔥🔥 LoginPage now importing REAL authService!');

// TypeScript Interfaces
interface LoginPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  onLoginSuccess: () => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
}

interface LoginFormData {
  email: string;
  password: string;
}

interface RegisterFormData {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  suggestions: string[];
}

// CSS Variables
const CSS_VARS = {
  primaryColor: '#253C8C',
  secondaryColor: '#F9D51D',
  textLight: '#718096',
  bgLight: '#f8f9ff',
  shadowLight: '0 4px 20px rgba(37, 60, 140, 0.1)',
  shadowHover: '0 8px 30px rgba(37, 60, 140, 0.2)',
  successColor: '#10b981',
  warningColor: '#f59e0b',
  errorColor: '#ef4444',
  weakColor: '#ef4444',
  fairColor: '#f59e0b',
  goodColor: '#10b981',
  strongColor: '#059669',
};

const LoginSection = styled.section`
  padding-top: 140px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 50%, #f0f4ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(37, 60, 140, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(249, 213, 29, 0.03) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const LoginContainer = styled.div`
  max-width: 650px;
  width: 100%;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 48px;
  box-shadow: ${CSS_VARS.shadowLight};
  text-align: center;
  border: 1px solid rgba(37, 60, 140, 0.1);
  animation: slideIn 0.6s ease;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 480px) {
    padding: 30px 20px;
    border-radius: 15px;
  }
`;

const LoginTitle = styled.h1`
  font-size: 2.2rem;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 8px;
  font-weight: 700;
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const LoginSubtitle = styled.p`
  color: ${CSS_VARS.textLight};
  margin-bottom: 35px;
  font-size: 1rem;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
  text-align: left;
  width: 100%;
`;

const HelperText = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-style: italic;
`;

const FormRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  align-items: flex-start;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FormColumn = styled.div`
  flex: 1 1 calc(50% - 12px);
  text-align: left;
  min-width: 0;
  max-width: calc(50% - 12px);
  
  @media (max-width: 768px) {
    flex: 1 1 100%;
    max-width: 100%;
    margin-bottom: 24px;
  }
`;

const FormLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 10px;
  min-height: 22px;
  width: 100%;
  
  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 8px;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${CSS_VARS.textLight};
  font-size: 1.1rem;
  z-index: 2;
`;

const FormInput = styled.input<{ $hasIcon?: boolean; $isValid?: boolean; $hasError?: boolean }>`
  width: 100%;
  height: 56px;
  padding: 16px 18px;
  padding-left: ${props => props.$hasIcon ? '48px' : '18px'};
  padding-right: ${props => props.$hasIcon ? '50px' : '18px'};
  border: 2px solid ${props => 
    props.$hasError ? CSS_VARS.errorColor : 
    props.$isValid ? CSS_VARS.successColor : 
    '#e2e8f0'
  };
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  box-sizing: border-box;
  display: block;

  &:focus {
    outline: none;
    border-color: ${props => 
      props.$hasError ? CSS_VARS.errorColor : 
      props.$isValid ? CSS_VARS.successColor : 
      CSS_VARS.primaryColor
    };
    box-shadow: 0 0 0 3px ${props => 
      props.$hasError ? 'rgba(239, 68, 68, 0.1)' : 
      props.$isValid ? 'rgba(16, 185, 129, 0.1)' : 
      'rgba(37, 60, 140, 0.1)'
    };
    transform: translateY(-1px);
  }

  &::placeholder {
    color: #a0aec0;
  }
  
  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
  }
`;

const ValidationIcon = styled.div<{ $isValid: boolean }>`
  position: absolute;
  right: 44px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.$isValid ? CSS_VARS.successColor : CSS_VARS.errorColor};
  font-size: 1rem;
  z-index: 3;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${CSS_VARS.textLight};
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  width: 28px;
  height: 28px;

  &:hover {
    color: ${CSS_VARS.primaryColor};
    background: rgba(37, 60, 140, 0.1);
  }
`;

const PasswordStrengthContainer = styled.div`
  margin-top: 10px;
  width: 100%;
  box-sizing: border-box;
`;

const PasswordStrengthBar = styled.div`
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const PasswordStrengthFill = styled.div<{ $strength: number; $color: string }>`
  height: 100%;
  width: ${props => props.$strength}%;
  background: ${props => props.$color};
  border-radius: 2px;
  transition: all 0.3s ease;
`;

const PasswordStrengthText = styled.div<{ $color: string }>`
  font-size: 0.8rem;
  color: ${props => props.$color};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PasswordSuggestions = styled.div`
  margin-top: 8px;
`;

const PasswordSuggestion = styled.div`
  font-size: 0.75rem;
  color: ${CSS_VARS.textLight};
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FieldError = styled.div`
  color: ${CSS_VARS.errorColor};
  font-size: 0.8rem;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 20px; /* Consistent height for error messages */
  
  svg {
    flex-shrink: 0; /* Prevent icon from shrinking */
  }
`;

const SubmitButton = styled.button<{ $isLoading?: boolean }>`
  width: 100%;
  padding: 14px;
  background: ${CSS_VARS.primaryColor};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    background: #1e3a8a;
    transform: translateY(-2px);
    box-shadow: ${CSS_VARS.shadowHover};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${CSS_VARS.primaryColor};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 8px 12px;
  border-radius: 6px;

  &:hover {
    color: #1e3a8a;
    background: rgba(37, 60, 140, 0.05);
  }
`;

const ErrorMessage = styled.div`
  color: ${CSS_VARS.errorColor};
  font-size: 0.9rem;
  margin-bottom: 15px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: slideIn 0.3s ease;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SuccessMessage = styled.div`
  color: ${CSS_VARS.successColor};
  font-size: 0.9rem;
  margin-bottom: 15px;
  padding: 12px 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: slideIn 0.3s ease;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ForgotPasswordLink = styled.button`
  background: none;
  border: none;
  color: ${CSS_VARS.primaryColor};
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 4px 8px;
  margin-top: 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #1e3a8a;
    background: rgba(37, 60, 140, 0.05);
    text-decoration: underline;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  
  svg {
    color: ${CSS_VARS.primaryColor};
    font-size: 28px;
  }
`;

const ModalTitle = styled.h2`
  color: ${CSS_VARS.primaryColor};
  font-size: 1.5rem;
  margin: 0;
`;

const ModalDescription = styled.p`
  color: ${CSS_VARS.textLight};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  ${props => props.$variant === 'secondary' ? `
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #e2e8f0;
    }
  ` : `
    background: ${CSS_VARS.primaryColor};
    color: white;
    border: none;
    
    &:hover:not(:disabled) {
      background: #1e3a8a;
      transform: translateY(-1px);
    }
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  `}
`;

const LoginPage: React.FC<LoginPageProps> = ({ 
  onHomeClick, 
  onBooksClick, 
  onVideoLearningClick, 
  onBlogsClick, 
  onLoginClick, 
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  onLoginSuccess,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  const [isLogin, setIsLogin] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});

  // Forgot password modal state
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState<boolean>(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState<boolean>(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<string>('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<string>('');

  // Change password modal state (for forced password change)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [changePasswordLoading, setChangePasswordLoading] = useState<boolean>(false);
  const [changePasswordError, setChangePasswordError] = useState<string>('');

  const [loginData, setLoginData] = useState<LoginFormData>({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState<RegisterFormData>({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Password strength calculation
  const calculatePasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    const suggestions: string[] = [];

    if (password.length < 8) {
      suggestions.push('Use at least 8 characters');
    } else {
      score += 1;
    }

    if (!/[a-z]/.test(password)) {
      suggestions.push('Add lowercase letters');
    } else {
      score += 1;
    }

    if (!/[A-Z]/.test(password)) {
      suggestions.push('Add uppercase letters');
    } else {
      score += 1;
    }

    if (!/[0-9]/.test(password)) {
      suggestions.push('Add numbers');
    } else {
      score += 1;
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      suggestions.push('Add symbols (!@#$%^&*)');
    } else {
      score += 1;
    }

    let label = 'Very Weak';
    let color = CSS_VARS.weakColor;

    if (score >= 4) {
      label = 'Strong';
      color = CSS_VARS.strongColor;
    } else if (score >= 3) {
      label = 'Good';
      color = CSS_VARS.goodColor;
    } else if (score >= 2) {
      label = 'Fair';
      color = CSS_VARS.fairColor;
    } else if (score >= 1) {
      label = 'Weak';
      color = CSS_VARS.weakColor;
    }

    return { score, label, color, suggestions };
  };

  const passwordStrength = !isLogin ? calculatePasswordStrength(registerData.password) : null;

  // Real-time validation
  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateField = (field: string, value: string): string => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'email':
        if (!value) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        if (value.trim().length < 10) return 'Please enter a valid phone number';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (isLogin) return '';
        if (value.length < 8) return 'Password must be at least 8 characters';
        return '';
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== registerData.password) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    // Validate fields
    const emailError = validateField('email', loginData.email);
    const passwordError = validateField('password', loginData.password);

    if (emailError || passwordError) {
      setFieldErrors({
        email: emailError,
        password: passwordError
      });
      setLoading(false);
      return;
    }

    try {
      const response = await authService.login(loginData.email, loginData.password);
      console.log('🚀 Login response received:', response);
      
      // Check what's stored in localStorage immediately after login
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      console.log('💾 Stored token:', storedToken ? 'Present' : 'Missing');
      console.log('💾 Stored user:', storedUser);
      
      // Check if user needs to change password
      if (response.user?.forcePasswordChange) {
        console.log('🔐 User needs to change password');
        setChangePasswordData({
          currentPassword: loginData.password, // Pre-fill with the temp password they just used
          newPassword: '',
          confirmNewPassword: ''
        });
        setShowChangePasswordModal(true);
        setSuccess('Login successful! Please change your password to continue.');
        setLoading(false);
        return;
      }
      
      setSuccess('Login successful! Welcome back to MathKnots.');
      
      // Call the success callback to update parent component
      if (onLoginSuccess) {
        console.log('📞 Calling onLoginSuccess callback');
        onLoginSuccess();
      } else {
        console.warn('⚠️ onLoginSuccess callback is not defined');
      }
    } catch (err: any) {
      console.error('❌ Login error:', err);
      // Use the actual error message from the server if available
      const errorMessage = err?.message || 'Invalid email or password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password request
  const handleForgotPasswordSubmit = async (): Promise<void> => {
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    if (!forgotPasswordEmail || !validateEmail(forgotPasswordEmail)) {
      setForgotPasswordError('Please enter a valid email address');
      setForgotPasswordLoading(false);
      return;
    }

    try {
      const response = await authService.requestPasswordReset(forgotPasswordEmail);
      setForgotPasswordSuccess(response.message || 'A temporary password has been sent to your email.');
      
      // Close modal after 3 seconds
      setTimeout(() => {
        setShowForgotPasswordModal(false);
        setForgotPasswordEmail('');
        setForgotPasswordSuccess('');
      }, 3000);
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setForgotPasswordError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Handle password change after forced reset
  const handleChangePasswordSubmit = async (): Promise<void> => {
    setChangePasswordLoading(true);
    setChangePasswordError('');

    const { currentPassword, newPassword, confirmNewPassword } = changePasswordData;

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setChangePasswordError('All fields are required');
      setChangePasswordLoading(false);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setChangePasswordError('New passwords do not match');
      setChangePasswordLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setChangePasswordError('New password must be at least 8 characters');
      setChangePasswordLoading(false);
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setChangePasswordError('Password must contain uppercase, lowercase, and number');
      setChangePasswordLoading(false);
      return;
    }

    try {
      await authService.changePassword(currentPassword, newPassword, confirmNewPassword);
      setShowChangePasswordModal(false);
      setSuccess('Password changed successfully! Welcome to MathKnots.');
      
      // Call the success callback to update parent component
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (err: any) {
      console.error('Change password error:', err);
      setChangePasswordError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // Open forgot password modal
  const openForgotPasswordModal = (): void => {
    setForgotPasswordEmail(loginData.email); // Pre-fill with login email if available
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
    setShowForgotPasswordModal(true);
  };

  const handleRegisterSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    // Validate all fields
    const nameError = validateField('name', registerData.name);
    const emailError = validateField('email', registerData.email);
    const phoneError = validateField('phone', registerData.phone);
    const passwordError = validateField('password', registerData.password);
    const confirmPasswordError = validateField('confirmPassword', registerData.confirmPassword);

    if (nameError || emailError || phoneError || passwordError || confirmPasswordError) {
      setFieldErrors({
        name: nameError,
        email: emailError,
        phone: phoneError,
        password: passwordError,
        confirmPassword: confirmPasswordError
      });
      setLoading(false);
      return;
    }

    // Check password strength
    if (passwordStrength && passwordStrength.score < 3) {
      setError('Please choose a stronger password for better security.');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.register({
        name: registerData.name,
        email: registerData.email,
        phone: registerData.phone,
        password: registerData.password,
        confirmPassword: registerData.confirmPassword
      });
      
      setSuccess('Account created successfully! Please sign in to continue.');
      console.log('Register response:', response);
      // Automatically switch to login mode after successful registration
      setTimeout(() => {
        setIsLogin(true);
        setSuccess('');
      }, 2000);
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof LoginFormData | keyof RegisterFormData, value: string): void => {
    if (isLogin) {
      setLoginData(prev => ({ ...prev, [field]: value }));
    } else {
      setRegisterData(prev => ({ ...prev, [field]: value }));
    }

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const toggleForm = (): void => {
    setIsLogin(!isLogin);
    setError('');
    setSuccess('');
    setFieldErrors({});
    setShowPassword(false);
  };

  return (
    <>
      <Header 
        onHomeClick={onHomeClick} 
        onBooksClick={onBooksClick} 
        onVideoLearningClick={onVideoLearningClick} 
        onBlogsClick={onBlogsClick} 
        onLoginClick={onLoginClick} 
        onAdminClick={onAdminClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        onLogout={onLogout}
        currentPage="login"
      />
      <LoginSection>
        <LoginContainer>
          <LoginCard>
            <LoginTitle>
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </LoginTitle>
            <LoginSubtitle>
              {isLogin 
                ? 'Sign in to access your MathKnots account' 
                : 'Join MathKnots to start your learning journey'
              }
            </LoginSubtitle>

            {error && (
              <ErrorMessage>
                <FiAlertCircle /> {error}
              </ErrorMessage>
            )}

            {success && (
              <SuccessMessage>
                <FiCheck /> {success}
              </SuccessMessage>
            )}

            <form onSubmit={isLogin ? handleLoginSubmit : handleRegisterSubmit}>
              {!isLogin ? (
                // Create Account Form - Horizontal Layout
                <>
                  <FormRow>
                    <FormColumn>
                      <FormLabel>
                        <FiUser /> Full Name
                      </FormLabel>
                      <InputWrapper>
                        <InputIcon>
                          <FiUser />
                        </InputIcon>
                        <FormInput
                          type="text"
                          placeholder="Enter your full name"
                          value={registerData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required
                          $hasIcon
                          $hasError={!!fieldErrors.name}
                          $isValid={!fieldErrors.name && registerData.name.length >= 2}
                        />
                        {registerData.name && (
                          <ValidationIcon $isValid={!fieldErrors.name && registerData.name.length >= 2}>
                            {!fieldErrors.name && registerData.name.length >= 2 ? <FiCheck /> : <FiX />}
                          </ValidationIcon>
                        )}
                      </InputWrapper>
                      {fieldErrors.name && (
                        <FieldError>
                          <FiAlertCircle /> {fieldErrors.name}
                        </FieldError>
                      )}
                    </FormColumn>

                    <FormColumn>
                      <FormLabel>
                        <FiMail /> Email Address
                      </FormLabel>
                      <InputWrapper>
                        <InputIcon>
                          <FiMail />
                        </InputIcon>
                        <FormInput
                          type="email"
                          placeholder="Enter your email"
                          value={registerData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          required
                          $hasIcon
                          $hasError={!!fieldErrors.email}
                          $isValid={validateEmail(registerData.email) && !fieldErrors.email}
                        />
                        {registerData.email && (
                          <ValidationIcon $isValid={validateEmail(registerData.email) && !fieldErrors.email}>
                            {validateEmail(registerData.email) && !fieldErrors.email ? <FiCheck /> : <FiX />}
                          </ValidationIcon>
                        )}
                      </InputWrapper>
                      {fieldErrors.email && (
                        <FieldError>
                          <FiAlertCircle /> {fieldErrors.email}
                        </FieldError>
                      )}
                    </FormColumn>
                  </FormRow>

                  <FormGroup>
                    <FormLabel>
                      <FiUser /> Phone Number
                    </FormLabel>
                    <InputWrapper>
                      <InputIcon>
                        <FiUser />
                      </InputIcon>
                      <FormInput
                        type="tel"
                        placeholder="Enter your phone number"
                        value={registerData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required
                        $hasIcon
                        $hasError={!!fieldErrors.phone}
                        $isValid={!fieldErrors.phone && registerData.phone.length >= 10}
                      />
                      {registerData.phone && (
                        <ValidationIcon $isValid={!fieldErrors.phone && registerData.phone.length >= 10}>
                          {!fieldErrors.phone && registerData.phone.length >= 10 ? <FiCheck /> : <FiX />}
                        </ValidationIcon>
                      )}
                    </InputWrapper>
                    {fieldErrors.phone && (
                      <FieldError>
                        <FiAlertCircle /> {fieldErrors.phone}
                      </FieldError>
                    )}
                    <HelperText>
                      🔒 We use your phone number for OTP verification only
                    </HelperText>
                  </FormGroup>

                  <FormRow>
                    <FormColumn>
                      <FormLabel>
                        <FiLock /> Password
                      </FormLabel>
                      <InputWrapper>
                        <InputIcon>
                          <FiLock />
                        </InputIcon>
                        <FormInput
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={registerData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          required
                          $hasIcon
                          $hasError={!!fieldErrors.password}
                          $isValid={!fieldErrors.password && passwordStrength?.score ? passwordStrength.score >= 3 : false}
                        />
                        <PasswordToggle
                          type="button"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </PasswordToggle>
                        {registerData.password && (
                          <ValidationIcon $isValid={!fieldErrors.password && passwordStrength?.score ? passwordStrength.score >= 3 : false}>
                            {!fieldErrors.password && passwordStrength?.score ? passwordStrength.score >= 3 : false ? <FiCheck /> : <FiX />}
                          </ValidationIcon>
                        )}
                      </InputWrapper>
                      {fieldErrors.password && (
                        <FieldError>
                          <FiAlertCircle /> {fieldErrors.password}
                        </FieldError>
                      )}
                      {registerData.password && (
                        <PasswordStrengthContainer>
                          <PasswordStrengthBar>
                            <PasswordStrengthFill 
                              $strength={(passwordStrength?.score || 0) * 20} 
                              $color={passwordStrength?.color || CSS_VARS.weakColor}
                            />
                          </PasswordStrengthBar>
                          <PasswordStrengthText $color={passwordStrength?.color || CSS_VARS.weakColor}>
                            {passwordStrength?.score && passwordStrength.score >= 3 ? <FiCheck /> : <FiAlertCircle />}
                            {passwordStrength?.label}
                          </PasswordStrengthText>
                          {passwordStrength?.suggestions && passwordStrength.suggestions.length > 0 && (
                            <PasswordSuggestions>
                              {passwordStrength.suggestions.slice(0, 2).map((suggestion, index) => (
                                <PasswordSuggestion key={index}>
                                  <FiAlertCircle /> {suggestion}
                                </PasswordSuggestion>
                              ))}
                            </PasswordSuggestions>
                          )}
                        </PasswordStrengthContainer>
                      )}
                    </FormColumn>

                    <FormColumn>
                      <FormLabel>
                        <FiLock /> Confirm Password
                      </FormLabel>
                      <InputWrapper>
                        <InputIcon>
                          <FiLock />
                        </InputIcon>
                        <FormInput
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          value={registerData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          required
                          $hasIcon
                          $hasError={!!fieldErrors.confirmPassword}
                          $isValid={!fieldErrors.confirmPassword && registerData.password === registerData.confirmPassword && registerData.confirmPassword.length > 0}
                        />
                        <PasswordToggle
                          type="button"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </PasswordToggle>
                        {registerData.confirmPassword && (
                          <ValidationIcon $isValid={!fieldErrors.confirmPassword && registerData.password === registerData.confirmPassword}>
                            {!fieldErrors.confirmPassword && registerData.password === registerData.confirmPassword ? <FiCheck /> : <FiX />}
                          </ValidationIcon>
                        )}
                      </InputWrapper>
                      {fieldErrors.confirmPassword && (
                        <FieldError>
                          <FiAlertCircle /> {fieldErrors.confirmPassword}
                        </FieldError>
                      )}
                    </FormColumn>
                  </FormRow>
                </>
              ) : (
                // Login Form - Vertical Layout
                <>
                  <FormGroup>
                    <FormLabel>
                      <FiMail /> Email Address
                    </FormLabel>
                    <InputWrapper>
                      <InputIcon>
                        <FiMail />
                      </InputIcon>
                      <FormInput
                        type="email"
                        placeholder="Enter your email"
                        value={loginData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        $hasIcon
                        $hasError={!!fieldErrors.email}
                        $isValid={validateEmail(loginData.email) && !fieldErrors.email}
                      />
                      {loginData.email && (
                        <ValidationIcon $isValid={validateEmail(loginData.email) && !fieldErrors.email}>
                          {validateEmail(loginData.email) && !fieldErrors.email ? <FiCheck /> : <FiX />}
                        </ValidationIcon>
                      )}
                    </InputWrapper>
                    {fieldErrors.email && (
                      <FieldError>
                        <FiAlertCircle /> {fieldErrors.email}
                      </FieldError>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      <FiLock /> Password
                    </FormLabel>
                    <InputWrapper>
                      <InputIcon>
                        <FiLock />
                      </InputIcon>
                      <FormInput
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={loginData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        required
                        $hasIcon
                        $hasError={!!fieldErrors.password}
                        $isValid={!fieldErrors.password && loginData.password.length > 0}
                      />
                      <PasswordToggle
                        type="button"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </PasswordToggle>
                      {loginData.password && (
                        <ValidationIcon $isValid={!fieldErrors.password && loginData.password.length > 0}>
                          {!fieldErrors.password && loginData.password.length > 0 ? <FiCheck /> : <FiX />}
                        </ValidationIcon>
                      )}
                    </InputWrapper>
                    {fieldErrors.password && (
                      <FieldError>
                        <FiAlertCircle /> {fieldErrors.password}
                      </FieldError>
                    )}
                    <ForgotPasswordLink type="button" onClick={openForgotPasswordModal}>
                      <FiKey size={14} /> Forgot Password?
                    </ForgotPasswordLink>
                  </FormGroup>
                </>
              )}

              <SubmitButton 
                type="submit" 
                disabled={loading} 
                $isLoading={loading}
              >
                {loading ? (
                  <>
                    <LoadingSpinner />
                    {isLogin ? 'Signing In...' : 'Creating Account...'}
                  </>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </SubmitButton>
            </form>

            <ToggleButton type="button" onClick={toggleForm}>
              {isLogin 
                ? "Don't have an account? Sign up" 
                : 'Already have an account? Sign in'
              }
            </ToggleButton>
          </LoginCard>
        </LoginContainer>
      </LoginSection>

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <ModalOverlay onClick={() => setShowForgotPasswordModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <FiKey />
              <ModalTitle>Reset Password</ModalTitle>
            </ModalHeader>
            <ModalDescription>
              Enter your email address and we'll send you a temporary password. 
              You'll be required to change it when you next log in.
            </ModalDescription>

            {forgotPasswordError && (
              <ErrorMessage>
                <FiAlertCircle /> {forgotPasswordError}
              </ErrorMessage>
            )}

            {forgotPasswordSuccess && (
              <SuccessMessage>
                <FiCheck /> {forgotPasswordSuccess}
              </SuccessMessage>
            )}

            <FormGroup>
              <FormLabel>
                <FiMail /> Email Address
              </FormLabel>
              <InputWrapper>
                <InputIcon>
                  <FiMail />
                </InputIcon>
                <FormInput
                  type="email"
                  placeholder="Enter your registered email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  $hasIcon
                  $isValid={validateEmail(forgotPasswordEmail)}
                  disabled={forgotPasswordLoading || !!forgotPasswordSuccess}
                />
              </InputWrapper>
            </FormGroup>

            <ModalButtonGroup>
              <ModalButton 
                $variant="secondary" 
                onClick={() => setShowForgotPasswordModal(false)}
                disabled={forgotPasswordLoading}
              >
                Cancel
              </ModalButton>
              <ModalButton 
                onClick={handleForgotPasswordSubmit}
                disabled={forgotPasswordLoading || !!forgotPasswordSuccess}
              >
                {forgotPasswordLoading ? (
                  <>
                    <LoadingSpinner /> Sending...
                  </>
                ) : (
                  <>
                    <FiMail /> Send Reset Email
                  </>
                )}
              </ModalButton>
            </ModalButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Change Password Modal (Forced) */}
      {showChangePasswordModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <FiLock />
              <ModalTitle>Change Your Password</ModalTitle>
            </ModalHeader>
            <ModalDescription>
              Your password has been reset. Please create a new secure password to continue.
            </ModalDescription>

            {changePasswordError && (
              <ErrorMessage>
                <FiAlertCircle /> {changePasswordError}
              </ErrorMessage>
            )}

            <FormGroup>
              <FormLabel>
                <FiLock /> Current/Temporary Password
              </FormLabel>
              <InputWrapper>
                <InputIcon>
                  <FiLock />
                </InputIcon>
                <FormInput
                  type="password"
                  placeholder="Enter your temporary password"
                  value={changePasswordData.currentPassword}
                  onChange={(e) => setChangePasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  $hasIcon
                  disabled={changePasswordLoading}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <FormLabel>
                <FiLock /> New Password
              </FormLabel>
              <InputWrapper>
                <InputIcon>
                  <FiLock />
                </InputIcon>
                <FormInput
                  type="password"
                  placeholder="Enter your new password"
                  value={changePasswordData.newPassword}
                  onChange={(e) => setChangePasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  $hasIcon
                  disabled={changePasswordLoading}
                />
              </InputWrapper>
              <HelperText>
                Must be at least 8 characters with uppercase, lowercase, and numbers
              </HelperText>
            </FormGroup>

            <FormGroup>
              <FormLabel>
                <FiLock /> Confirm New Password
              </FormLabel>
              <InputWrapper>
                <InputIcon>
                  <FiLock />
                </InputIcon>
                <FormInput
                  type="password"
                  placeholder="Confirm your new password"
                  value={changePasswordData.confirmNewPassword}
                  onChange={(e) => setChangePasswordData(prev => ({ ...prev, confirmNewPassword: e.target.value }))}
                  $hasIcon
                  $isValid={changePasswordData.confirmNewPassword.length > 0 && changePasswordData.newPassword === changePasswordData.confirmNewPassword}
                  disabled={changePasswordLoading}
                />
              </InputWrapper>
            </FormGroup>

            <ModalButtonGroup>
              <ModalButton 
                onClick={handleChangePasswordSubmit}
                disabled={changePasswordLoading}
                style={{ flex: 1 }}
              >
                {changePasswordLoading ? (
                  <>
                    <LoadingSpinner /> Changing...
                  </>
                ) : (
                  <>
                    <FiCheck /> Change Password
                  </>
                )}
              </ModalButton>
            </ModalButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default LoginPage; 